import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc, Discount, updatePaymentInfoDocPayload } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { FormInput } from '../../../../components/forms/FormInput';
import { Button } from '../../../../components/buttons/Button';
import { getProduct } from '../../../../data/products/getProduct';
import { requestDomainDiscount } from '../../../../data/discounts/requestDomainDiscount';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { getUserDomainDiscount } from '../../../../data/discounts/getUserDomainDiscount';
import { markDiscountUsed } from '../../../../data/discounts/markCouponUsed';
import { usePaymentIntent } from '../../../../hooks/usePaymentIntent';
import { getStripePromise } from '../../../../data/payment/stripeLoader';
import { StripeCheckout } from '../../../../components/payment/StripeCheckout';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import { toast } from 'sonner';
import { FormSelect } from '../../../../components/forms/FormSelect';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
export const Route = createFileRoute('/_authenticated/$patientId/payment/selfPay')({
  component: SelfPay,
});

function SelfPay() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const { mutate: mutateChartDoc } = useUpdateChartSection();

  const [alreadyPaid, setAlreadyPaid] = React.useState(false);
  const [globalDiscounts, setGlobalDiscounts] = React.useState<Discount[]>([]);
  const [productCost, setProductCost] = React.useState(349);
  const [discountAmt, setDiscountAmt] = React.useState(0);
  const [discountApplied, setDiscountApplied] = React.useState(false);
  const [readyToPay, setReadyToPay] = React.useState(false);
  const [applyingDiscount, setApplyingDiscount] = React.useState(false);
  const [originalProductCost, setOriginalProductCost] = React.useState(999);
  const [checkingDiscount, setCheckingDiscount] = React.useState(false);
  const [requestingDiscount, setRequestingDiscount] = React.useState(false);
  const [hasDiscount, setHasDiscount] = React.useState(false);
  const [productLoadError, setProductLoadError] = React.useState(false);
  const [partnerOrg, setPartnerOrg] = React.useState('select');
  const [referralCode, setReferralCode] = React.useState('select');
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      toast.error(`Incomplete Information: ${res.incompleteMessage}`, { duration: 8000 });
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      toast.error('There was an error loading your information. Please try again or contact support.', { duration: 8000 });
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          toast.error(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
            { duration: 8000 },
          );
        },
      },
    );
  };

  const codeForm = useForm({
    defaultValues: {
      email: '',
      discountCode: '',
    },
    onSubmit: async ({ value }) => {
      await handleDomainDiscountRequest(value.email);
    },
  });

  const handleDomainDiscountRequest = async (email: string) => {
    setRequestingDiscount(true);
    // SET EMAIL AS CONTACT EMAIL
    mutatePatientDoc(
      {
        patientId: patientId,
        contactEmail: email,
      },
      {
        onSuccess: async () => {
          // REQUEST DOMAIN-SPECIFIC DISCOUNT
          const domain = email.split('@')[1];
          try {
            const res = await requestDomainDiscount(domain, patientId, chart.PRODUCT_INFO.selectedProduct);
            setRequestingDiscount(false);
            if (res.discountCode) {
              setHasDiscount(true);
              toast.success(
                'We received your request for a discount. Please check your email for the discount code. If you do not receive an email within 24 hours, please contact support.',
                { duration: 8000 },
              );
            }
          } catch (error) {
            setRequestingDiscount(false);
            toast.error((error as Error).message || 'Unable to process discount request. Please try again or contact support.', { duration: 8000 });
          }
        },
        onError: (error) => {
          setRequestingDiscount(false);
          Sentry.captureException(error);
          toast.error('There was an error saving your email. Please try again or contact support.', { duration: 8000 });
        },
      },
    );
  };

  const updatePaymentInfoDoc = (payload: updatePaymentInfoDocPayload) => {
    mutateChartDoc(
      {
        patientId: patientId,
        docData: {
          PAYMENT_INFO: {
            alreadyPaid: payload.alreadyPaid,
            amountPaid: payload.amountPaid,
            couponCode: payload.couponCode,
            discountApplied: payload.discountApplied,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          toast.error('There was an error saving your payment information. Please try again or contact support.', { duration: 8000 });
        },
      },
    );
  };

  const applyDiscount = async () => {
    // PULL THE CODE FROM THE FORM
    const code = codeForm.state.values.discountCode;

    // CHECK IF DOMAIN DISCOUNT EXISTS FOR THIS USER
    const existingDiscount = await getUserDomainDiscount(patientId);

    // IF SO, AND THEY MATCH; APPLY THE DISCOUNT
    if (existingDiscount?.discountCode === code && !existingDiscount.used) {
      const amt = parseInt(existingDiscount.discount);
      setDiscountAmt(amt);
      setProductCost(originalProductCost - amt);
      setDiscountApplied(true);
      try {
        await markDiscountUsed({
          code: existingDiscount.discountCode,
          discount: existingDiscount.discount,
          domain: existingDiscount.domain,
          product: existingDiscount.product,
          uid: patientId,
        });
        updatePaymentInfoDoc({
          alreadyPaid: originalProductCost - amt == 0 ? true : false,
          amountPaid: originalProductCost - amt,
          couponCode: codeForm.state.values.discountCode,
          discountApplied: existingDiscount.discount,
        });
      } catch (error) {
        // Revert UI state on failure
        setDiscountAmt(0);
        setProductCost(originalProductCost);
        setDiscountApplied(false);
        toast.error((error as Error).message || 'Unable to apply discount code. Please try again or contact support.', { duration: 8000 });
      }
    } else {
      // CHECK IF THE CODE IS A GLOBAL DISCOUNT CODE
      setCheckingDiscount(true);
      const globalDiscount = globalDiscounts.find((item) => item.name === code);
      if (globalDiscount) {
        // IF SO, APPLY THE DISCOUNT
        const amt = parseInt(globalDiscount.discount);
        setDiscountAmt(amt);
        setProductCost(originalProductCost - amt);
        setDiscountApplied(true);
        try {
          await markDiscountUsed({
            code: globalDiscount.name,
            discount: globalDiscount.discount,
            domain: 'global',
            product: chart.PRODUCT_INFO.selectedProduct,
            uid: patientId,
          });
          updatePaymentInfoDoc({
            alreadyPaid: originalProductCost - amt === 0 ? true : false,
            amountPaid: originalProductCost - amt,
            couponCode: codeForm.state.values.discountCode,
            discountApplied: amt,
          });
        } catch (error) {
          // Revert UI state on failure
          setDiscountAmt(0);
          setProductCost(originalProductCost);
          setDiscountApplied(false);
          toast.error((error as Error).message || 'Unable to apply discount code. Please try again or contact support.', { duration: 8000 });
        }
        setCheckingDiscount(false);
      } else {
        toast.error(
          'Invalid discount code. If you believe this is an error, please refresh the page and try again. If you continue to have issues, please contact support.',
          { duration: 8000 },
        );
        setCheckingDiscount(false);
      }
    }
  };

  // ⚠️ PAY NOW STAYS HIDDEN UNTIL A DISCOUNT HAS FULLY SETTLED (CU-17tm466h6uv).
  // applyDiscount shows the discounted price BEFORE markDiscountUsed returns and
  // reverts it if that call fails. If Pay Now were clicked in between, the
  // revert would re-price the intent under a mounted form — reachable, because
  // commerceApi can cold-start (found by adversarial review). Gating here means
  // a revert can only ever happen before Pay Now; key={clientSecret} below
  // remains the backstop.
  const handleApplyDiscount = async () => {
    setApplyingDiscount(true);
    try {
      await applyDiscount();
    } finally {
      setApplyingDiscount(false);
    }
  };

  const showPayNowButton = () => {
    // PATIENT ALREADY PAID
    if (productCost == 0) {
      return false;
    }
    // PATIENT IS PAYING IN FULL
    if (partnerOrg == 'no' && referralCode == 'no') {
      return true;
    }
    // PATIENT SUCCESSFULLY APPLIED A PARTIAL DISCOUNT
    if (hasDiscount && discountAmt != 0) {
      return true;
    }
    return false;
  };

  // CREATE THE STRIPE PAYMENT INTENT ONCE THE PATIENT CLICKS PAY NOW
  // Receipts go to accountEmail — set at account creation and immutable. The old
  // contactEmail is overwritten by the domain-discount flow with whatever org
  // address was typed (even when that request is then rejected), and its guard
  // made this request silently never fire when it was absent. (CU-17tm466h6uv)
  const paymentIntent = usePaymentIntent({
    enabled: readyToPay,
    amountCents: Math.round(productCost * 100), // CONVERT TO CENTS FOR STRIPE
    receiptEmail: patientInfo?.accountEmail,
  });
  const clientSecret = paymentIntent.clientSecret;

  // ONCE PAY NOW IS CLICKED THE AMOUNT IS LOCKED. A mounted <Elements> cannot
  // change its client secret, so a discount applied after Pay Now would leave the
  // form charging the ORIGINAL amount while the page shows the discounted one.
  // Changing the discount goes through "Change or add a discount", which unmounts
  // the payment form first. (CU-17tm466h6uv)
  const discountLocked = readyToPay;

  // START LOADING STRIPE.JS AS SOON AS THE PAGE IS UP, in parallel with the
  // patient's choices, instead of only after the PaymentIntent returns. Route
  // preloading never mounts this component, so the CU-86afqb0pg deferral (no
  // Stripe on other pages) still holds. Failures are handled by StripeCheckout.
  const needsPayment = !!chart && !chart.PAYMENT_INFO?.alreadyPaid;
  React.useEffect(() => {
    if (needsPayment) {
      getStripePromise().catch(() => {});
    }
  }, [needsPayment]);

  // SET THE GLOBAL DISCOUNTS & CHECK FOR EXISTING DISCOUNT CODE
  React.useEffect(() => {
    if (chart?.PAYMENT_INFO?.alreadyPaid) {
      setAlreadyPaid(chart.PAYMENT_INFO.alreadyPaid);
    }
    const init = async () => {
      let productKey = 'vip';
      switch (chart?.PRODUCT_INFO?.selectedProduct) {
        case 'essential':
          productKey = 'essential';
          break;
        case 'vital':
          productKey = 'vital';
          break;
        case 'vip':
          productKey = 'vip';
          break;
        case 'Self Guided Package':
          productKey = 'essential';
          break;
        case 'Vital Package':
          productKey = 'vital';
          break;
        case 'Physician Guided Package':
          productKey = 'vip';
          break;
        default:
          break;
      }
      try {
        setProductLoadError(false);
        const productRes = await getProduct(productKey);
        if (productRes && productRes.coupons?.length > 0) {
          setGlobalDiscounts([...productRes.coupons.filter((item) => item.active && item.type === 'global')]);
          setOriginalProductCost(productRes.oneTimePrice);
          setProductCost(productRes.oneTimePrice);
        }
      } catch (error) {
        setProductLoadError(true);
        toast.error('Unable to load pricing information. Please try refreshing the page. If this issue persists, contact support.', { duration: 8000 });
      }

      // Check for existing discount code (e.g. patient refreshed mid-flow)
      try {
        const existingDiscount = await getUserDomainDiscount(patientId);
        if (existingDiscount?.discountCode && !existingDiscount.used) {
          setHasDiscount(true);
          setPartnerOrg('yes');
          const emailMsg = existingDiscount.recipientEmail
            ? ` to ${existingDiscount.recipientEmail}`
            : '';
          toast.info(`A discount code was previously sent${emailMsg}. Enter it below to apply your discount.`, { duration: 8000 });
        }
      } catch {
        // No existing discount — expected for most patients
      }
    };
    if (!chart?.PAYMENT_INFO?.alreadyPaid && chart?.PRODUCT_INFO?.selectedProduct) {
      init();
    }
  }, [chart?.PRODUCT_INFO?.selectedProduct, chart?.PAYMENT_INFO?.alreadyPaid]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        {chart?.PRODUCT_INFO?.selectedProduct ? (
          <div id="payment-page" className="flex w-screen flex-col items-center justify-center">
            <Heading id="payment-page-header" textContent="Payment Page" />
            <div id="payment-page-main" className="flex w-5/6 flex-col justify-around">
              <div className="flex flex-col">
                <div id="pricing-info">
                  <p className="text-xl font-bold" id="product-name">
                    Product: {chart.PRODUCT_INFO?.selectedProduct}
                  </p>
                  <p className="text-xl font-bold" id="product-initial-cost">
                    Cost: {alreadyPaid ? 'PAID' : originalProductCost === 999 ? 'Loading' : '$ ' + originalProductCost}
                  </p>
                  {discountAmt > 0 && (
                    <p className="text-xl font-bold" id="product-discount">
                      Discount: ${discountAmt}
                    </p>
                  )}
                  {discountAmt > 0 && (
                    <p className="text-xl font-bold" id="product-final-cost">
                      Final Cost: ${productCost}
                    </p>
                  )}
                </div>
                {originalProductCost === 999 && !productLoadError ? (
                  <div className="ml-2">
                    <svg
                      aria-hidden="true"
                      className="inline h-10 w-10 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  </div>
                ) : productLoadError ? (
                  <div className="my-4 rounded-md border border-red-300 bg-red-50 p-4">
                    <p className="text-lg text-red-800">
                      Unable to load pricing information. Please refresh the page to try again.
                    </p>
                    <p className="mt-2 text-sm text-red-600">
                      If this issue persists, please contact support.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (discountLocked) return;
                      codeForm.handleSubmit();
                    }}
                  >
                    {/* A disabled fieldset natively disables every select, input and button inside it. */}
                    <fieldset id="discount-fields" disabled={discountLocked} className="disabled:opacity-60">
                    <FormSelect
                      label="Are you arriving from one of our partner organizations entitling you to a discount?"
                      id="partner-org"
                      name="Partner"
                      value={partnerOrg}
                      required={true}
                      onBlur={(e) => setPartnerOrg(e.target.value)}
                      onChange={(e) => setPartnerOrg(e.target.value)}
                      options={[
                        { name: 'Please Select...', value: 'select' },
                        { name: 'No', value: 'no' },
                        { name: 'Yes', value: 'yes' },
                      ]}
                    />
                    {partnerOrg == 'yes' && (
                      <div id="org-coupon-info" className="my-4">
                        <p className="text-2xl font-bold">
                          Please enter <span className="italic">your organizational email </span>address below then
                          click the &quot;Request Discount&quot; button
                        </p>
                        <div className="flex flex-row">
                          <codeForm.Field
                            name="email"
                            children={(field) => (
                              <>
                                <FormInput
                                  label="Email"
                                  id="discount-email"
                                  name={field.name}
                                  value={field.state.value}
                                  handleBlur={field.handleBlur}
                                  handleChange={(e) => field.handleChange(e.target.value)}
                                  required={false}
                                />
                              </>
                            )}
                          />
                          <Button id="request-discount" type="submit" textContent="Request Discount" />
                          {requestingDiscount && <div>Requesting Discount...</div>}
                        </div>
                      </div>
                    )}

                    {partnerOrg == 'no' && (
                      <FormSelect
                        label="Do you have a referral code from an advisor or other source?"
                        id="referral-code"
                        name="Referral Code"
                        value={referralCode}
                        required={true}
                        onBlur={(e) => {
                          setReferralCode(e.target.value);
                          setHasDiscount(e.target.value === 'yes' ? true : false);
                        }}
                        onChange={(e) => {
                          setReferralCode(e.target.value);
                          setHasDiscount(e.target.value === 'yes' ? true : false);
                        }}
                        options={[
                          { name: 'Please Select...', value: 'select' },
                          { name: 'No', value: 'no' },
                          { name: 'Yes', value: 'yes' },
                        ]}
                      />
                    )}

                    {hasDiscount && (
                      <div id="code-input">
                        <p className="my-4 mt-6 text-2xl font-bold">
                          Please enter your code below and click the &quot;Apply Code&quot; button.
                        </p>
                        <div className="flex flex-row items-center">
                          <codeForm.Field
                            name="discountCode"
                            children={(field) => (
                              <FormInput
                                label="Referral Code"
                                id="discount-code"
                                name={field.name}
                                value={field.state.value}
                                handleBlur={field.handleBlur}
                                handleChange={(e) => field.handleChange(e.target.value)}
                                required={false}
                              />
                            )}
                          />
                          <Button
                            id="discount-apply"
                            type="button"
                            textContent={discountApplied ? 'Code Applied' : 'Apply Code'}
                            onClick={handleApplyDiscount}
                            disabled={discountApplied || applyingDiscount}
                          />
                          {checkingDiscount && (
                            <div>
                              <p className="ml-4 text-xl">Checking Discount...</p>
                            </div>
                          )}
                          {!alreadyPaid && globalDiscounts?.length === 0 && (
                            <div>
                              <p className="ml-4 text-xl">Updated Referral Codes list. Please refresh to continue.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    </fieldset>
                  </form>
                )}
              </div>
              {readyToPay && (
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    id="change-discount"
                    onClick={() => setReadyToPay(false)}
                    className="text-lg text-sky-700 underline hover:text-sky-900"
                  >
                    Change or add a discount
                  </button>
                </div>
              )}
              {readyToPay && (paymentIntent.status === 'loading' || paymentIntent.status === 'slow') && (
                <div id="payment-intent-loading" className="py-4 text-center">
                  <p className="text-lg text-gray-600">Connecting to payment provider...</p>
                  {paymentIntent.status === 'slow' && (
                    <p id="payment-intent-slow" className="mt-2 text-gray-600">
                      This can take up to a minute — please keep this page open.
                    </p>
                  )}
                </div>
              )}
              {readyToPay && paymentIntent.status === 'error' && (
                <div
                  id="payment-intent-error"
                  className="my-4 rounded-md border border-red-300 bg-red-50 p-6 text-center"
                >
                  <p className="text-lg font-semibold text-red-800">
                    {paymentIntent.error?.kind === 'rejected'
                      ? 'We could not start your payment.'
                      : 'We could not connect to our payment provider.'}
                  </p>
                  <p className="mt-2 text-sm text-red-600">
                    This may be caused by a slow connection or a temporary outage. Please try again.
                  </p>
                  <button
                    type="button"
                    id="payment-intent-retry"
                    onClick={paymentIntent.retry}
                    className="mt-4 rounded-md bg-sky-700 px-6 py-2 text-white hover:bg-sky-800"
                  >
                    Try Again
                  </button>
                  <p className="mt-3 text-xs text-gray-500">
                    If this issue persists, please contact{' '}
                    <a href="mailto:info@mideohealth.com" className="underline">
                      info@mideohealth.com
                    </a>
                  </p>
                </div>
              )}
              {readyToPay && clientSecret ? (
                <>
                  <div id="stripe-checkout" className="mt-8">
                    {/* Keyed on the secret: <Elements> cannot change its secret after mount,
                        so a new secret must mean a fresh form — never a silently stale one. */}
                    <StripeCheckout
                      key={clientSecret}
                      clientSecret={clientSecret}
                      patientId={patientId}
                      amountPaid={productCost}
                      couponCode={discountApplied ? codeForm.state.values.discountCode : null}
                      discountApplied={discountAmt}
                    />
                  </div>
                </>
              ) : (
                <div className="mt-10 flex flex-col items-center justify-center">
                  {showPayNowButton() && !readyToPay && !applyingDiscount && (
                    <>
                      <p className="text-xl">
                        To avoid being overcharged, you should only click &quot;Pay Now&quot; after your discount has
                        been applied.
                      </p>
                      <p className="text-xl">
                        If you do not have any discounts, you can click the &quot;Pay Now&quot; button when ready.
                      </p>
                      <div>
                        <Button
                          type="button"
                          id="ready-to-pay"
                          textContent="Pay Now"
                          disabled={false}
                          onClick={() => setReadyToPay(true)}
                        />
                      </div>
                    </>
                  )}
                  {alreadyPaid || productCost == 0 ? (
                    // <NavForward to={nextPageURL} disabled={false} />
                    <div className="flex flex-col items-center">
                      <button
                        id="submit-form-info"
                        title="Submit Form Info"
                        onClick={handleNextButtonClick}
                        disabled={false}
                        className={classNames.nextButton}
                      >
                        Next
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <p>Unable to determine product type</p>
          </div>
        )}
      </>
    );
  }

  return <div>Loading...</div>;
}
